import { queryKeys } from '@constants/queryKeys';
import { useCentreList, useDeleteMasterItem } from '@features/settings';
import { useRealtimeInvalidate } from '@hooks/useRealtimeInvalidate';
import { AddOutlined, DeleteOutlined, LocationOnOutlined } from '@mui/icons-material';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import type { CentreItem } from '@services/master.service';
import { useAppShellStore } from '@store/appShellStore';
import { useState } from 'react';

import { AddCentreDialog } from './AddCentreDialog';

interface ManageCentresDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * "Manage Centres" — opened from the top-bar Centre switcher (admin only).
 * Lists the centres of the CURRENTLY SELECTED main centre with a Delete action;
 * the header's "New Centre" button opens `AddCentreDialog` nested on top (which
 * can file the new centre under any main centre). Creating a centre invalidates
 * this list's query (via useCreateCentre's onSuccess) so it refreshes
 * automatically, and the top-bar switcher itself picks up the new centre via the
 * existing `centres:changed` realtime handler in App.tsx.
 */
export const ManageCentresDialog = ({ open, onClose }: ManageCentresDialogProps) => {
  const mainCentreId = useAppShellStore((s) => s.mainCentreId);
  const mainCentreName = useAppShellStore(
    (s) => s.mainCentres.find((m) => m._id === s.mainCentreId)?.name ?? '',
  );
  const { data, isLoading } = useCentreList(mainCentreId);
  const deleteItem = useDeleteMasterItem('centres');
  const [newCentreOpen, setNewCentreOpen] = useState(false);
  const [pendingRemove, setPendingRemove] = useState<CentreItem | null>(null);

  // Live-refresh while the modal is open, mirroring MasterListSection's own
  // realtime wiring for the same `master.list('centres')` query key.
  useRealtimeInvalidate('centres', [queryKeys.master.list('centres')]);

  const centres = data ?? [];

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        slotProps={{ paper: { sx: { borderRadius: '16px', p: '8px', width: { xs: 'calc(100vw - 32px)', sm: 480 }, maxWidth: 'calc(100vw - 32px)' } } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', px: '20px', pt: '16px', pb: '4px' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={(theme) => ({ fontSize: 16, fontWeight: 700, color: theme.palette.grey[900] })}>
              Manage Centres
            </Typography>
            {mainCentreName && (
              <Typography sx={(theme) => ({ fontSize: 12, color: theme.palette.grey[500], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' })}>
                in {mainCentreName}
              </Typography>
            )}
          </Box>
          <Button
            onClick={() => setNewCentreOpen(true)}
            startIcon={<AddOutlined sx={{ fontSize: 16 }} />}
            sx={(theme) => ({
              bgcolor: theme.palette.primary.main, borderRadius: '8px', color: 'white', fontSize: 13, fontWeight: 600,
              textTransform: 'none', px: '14px', py: '7px', boxShadow: 'none', flexShrink: 0,
              '&:hover': { bgcolor: theme.palette.primary.dark, boxShadow: 'none' },
            })}
          >
            New Centre
          </Button>
        </Box>
        <DialogContent sx={{ minHeight: 100 }}>
          {isLoading ? (
            <Box sx={{ py: '24px', display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={20} />
            </Box>
          ) : centres.length === 0 ? (
            <Typography sx={(theme) => ({ fontSize: 13.5, color: theme.palette.grey[500], textAlign: 'center', py: '24px' })}>
              No centres in this main centre yet — add one to get started.
            </Typography>
          ) : (
            centres.map((c, idx) => (
              <Box
                key={c._id}
                sx={(theme) => ({
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
                  py: '12px', borderBottom: idx < centres.length - 1 ? `1px solid ${theme.palette.grey[100]}` : 'none',
                })}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', minWidth: 0 }}>
                  <LocationOnOutlined sx={(theme) => ({ fontSize: 16, color: theme.palette.grey[400], flexShrink: 0 })} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={(theme) => ({ fontSize: 14, fontWeight: 600, color: theme.palette.grey[700], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' })}>
                      {c.name}{c.code ? ` (${c.code})` : ''}
                    </Typography>
                    {!c.active && (
                      <Typography sx={(theme) => ({ fontSize: 11, color: theme.palette.error.main, fontWeight: 600 })}>Inactive</Typography>
                    )}
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  disabled={!c.active}
                  onClick={() => setPendingRemove(c)}
                  sx={(theme) => ({
                    width: 30, height: 30, borderRadius: '7px', flexShrink: 0,
                    bgcolor: '#fff1f0', color: theme.palette.error.main,
                    '&:hover': { bgcolor: '#fee2e2' },
                    '&.Mui-disabled': { opacity: 0.35 },
                  })}
                >
                  <DeleteOutlined sx={{ fontSize: 17 }} />
                </IconButton>
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions sx={{ px: '20px', pb: '16px' }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={(theme) => ({
              borderRadius: '8px', color: theme.palette.grey[700], fontSize: 13, fontWeight: 600,
              textTransform: 'none', px: '18px', py: '9px', borderColor: theme.palette.grey[200],
              '&:hover': { bgcolor: theme.palette.grey[50], borderColor: theme.palette.grey[300] },
            })}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={!!pendingRemove}
        onClose={() => setPendingRemove(null)}
        slotProps={{ paper: { sx: { borderRadius: '14px', p: '8px', width: 340, maxWidth: 'calc(100vw - 32px)' } } }}
      >
        <DialogTitle sx={(theme) => ({ fontSize: 15, fontWeight: 700, color: theme.palette.grey[900], pb: '6px' })}>
          Remove "{pendingRemove?.name}"?
        </DialogTitle>
        <DialogContent>
          <Typography sx={(theme) => ({ fontSize: 13.5, color: theme.palette.grey[500] })}>
            This will deactivate the centre — it will no longer be selectable from the top-bar switcher.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: '20px', pb: '16px', gap: '8px' }}>
          <Button
            onClick={() => setPendingRemove(null)}
            sx={(theme) => ({
              fontSize: 13, fontWeight: 600, textTransform: 'none', color: theme.palette.grey[700], borderRadius: '8px',
              '&:hover': { bgcolor: theme.palette.grey[100] },
            })}
          >
            Cancel
          </Button>
          <Button
            onClick={() => { if (pendingRemove) deleteItem.mutate(pendingRemove._id); setPendingRemove(null); }}
            variant="contained"
            sx={(theme) => ({
              fontSize: 13, fontWeight: 600, textTransform: 'none',
              bgcolor: theme.palette.error.main, color: 'white', borderRadius: '8px', boxShadow: 'none',
              '&:hover': { bgcolor: theme.palette.status.error.text, boxShadow: 'none' },
            })}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <AddCentreDialog open={newCentreOpen} onClose={() => setNewCentreOpen(false)} />
    </>
  );
};
